import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import styles from './Page.module.css';
import { fmtQtd } from '../utils/formatQtd';
import ModalExportarLista from '../components/common/ModalExportarLista';
import ModalExportarBalanca from '../components/common/ModalExportarBalanca';

// Célula editável da grade (clique → vira input/select; Enter ou sair do
// campo salva; Esc cancela). Só usada pelas 4 colunas liberadas pra edição
// direta aqui (NCM, CST/CSOSN, Grupo, Subgrupo) — o resto do cadastro
// continua só pelo Cad. Produtos completo.
// `linha`/`coluna` só são usados nas colunas de TEXTO que aceitam navegação
// tipo planilha (NCM, CST/CSOSN, CEST) — ↓/↑ salva a célula atual e já abre
// a mesma coluna na linha seguinte/anterior, achada via [data-cel] no DOM
// (células são componentes irmãos isolados, sem estado compartilhado; é
// mais simples que levantar esse estado todo pro componente pai). Select
// (Grupo/Subgrupo) não entra nisso — ↓/↑ ali continua navegando as opções
// do combo, comportamento nativo que não faz sentido atropelar.
function CelulaEditavel({ valor, exibicao, tipo = 'texto', opcoes, onSalvar, placeholder = '—', subinfo, linha, coluna }) {
  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(valor ?? '');

  useEffect(() => { if (!editando) setRascunho(valor ?? ''); }, [valor, editando]);

  const dataCel = coluna != null && linha != null ? `${coluna}-${linha}` : undefined;

  if (!editando) {
    return (
      <td data-cel={dataCel} onClick={() => setEditando(true)} title="Clique para editar" style={{ cursor: 'pointer' }}>
        <div>{exibicao || <span style={{ color: '#aaa' }}>{placeholder}</span>}</div>
        {subinfo && <div style={{ fontSize: 10, color: '#999', marginTop: 1 }}>{subinfo}</div>}
      </td>
    );
  }

  const confirmar = () => {
    setEditando(false);
    const mudou = String(rascunho ?? '') !== String(valor ?? '');
    if (mudou) onSalvar(rascunho === '' ? null : rascunho);
  };
  const cancelar = () => { setRascunho(valor ?? ''); setEditando(false); };
  const irPara = (linhaAlvo) => {
    // setTimeout(0): dá tempo do React desmontar o input desta célula (o
    // confirmar() já rodou síncrono) antes de clicar na vizinha — clicar
    // direto arriscaria competir com o re-render.
    setTimeout(() => {
      document.querySelector(`[data-cel="${coluna}-${linhaAlvo}"]`)?.click();
    }, 0);
  };
  const onKeyDown = (e) => {
    if (e.key === 'Enter') { confirmar(); return; }
    if (e.key === 'Escape') { cancelar(); return; }
    if (tipo === 'texto' && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      confirmar();
      irPara(linha + (e.key === 'ArrowDown' ? 1 : -1));
    }
  };

  return (
    <td style={{ padding: '2px 4px' }}>
      {tipo === 'select' ? (
        <select autoFocus value={rascunho ?? ''} onChange={(e) => setRascunho(e.target.value)}
          onBlur={confirmar} onKeyDown={onKeyDown} style={{ width: '100%' }}>
          <option value="">—</option>
          {(opcoes || []).map((o) => <option key={o.id} value={o.id}>{o.nome}</option>)}
        </select>
      ) : (
        <input autoFocus value={rascunho ?? ''} onChange={(e) => setRascunho(e.target.value)}
          onBlur={confirmar} onKeyDown={onKeyDown} style={{ width: '100%' }} />
      )}
    </td>
  );
}

const COLUNAS_EXPORT = [
  { key: 'codigo_interno', label: 'Código' },
  { key: 'codigo_barras',  label: 'Código de Barras' },
  { key: 'nome',           label: 'Nome' },
  { key: 'unidade',        label: 'Unidade' },
  { key: 'tipo',           label: 'Tipo', padrao: false },
  { key: 'grupo_nome',     label: 'Grupo', padrao: false },
  { key: 'obs_fiscal',     label: 'Obs. Fiscal', padrao: false },
  { key: 'preco_venda',    label: 'Preço Venda',
    render: p => Number(p.preco_venda || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
  { key: 'preco_custo',    label: 'Preço Custo',
    render: p => Number(p.preco_custo || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
  { key: 'estoque_atual',  label: 'Estoque Atual',
    render: p => fmtQtd(parseFloat(p.estoque_atual || 0), p.unidade_fraciona) },
  { key: 'estoque_minimo', label: 'Estoque Mínimo', padrao: false },
];

const COLUNAS_PESADOS = [
  { key: 'codigo_interno', label: 'Cód. Interno' },
  { key: 'nome',           label: 'Descrição' },
  { key: 'unidade',        label: 'Unidade' },
  { key: 'preco_venda',    label: 'Preço',
    render: p => Number(p.preco_venda || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
];

const ESTOQUE_STATUS_OPCOES = [
  { value: '',          label: 'Estoque: todos' },
  { value: 'negativo',  label: 'Estoque < 0' },
  { value: 'zero',      label: 'Estoque = 0' },
  { value: 'positivo',  label: 'Estoque > 0' },
];

// Lista de consulta — o cadastro de produtos é feito em Cad. Produtos
// (tela completa) ou pela importação de XML/DF-e.
export default function Produtos() {
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState('');
  const [modalExportar, setModalExportar] = useState(false);
  const [grupoId, setGrupoId] = useState('');
  const [subgrupoId, setSubgrupoId] = useState('');
  const [fornecedorId, setFornecedorId] = useState('');
  const [estoqueStatus, setEstoqueStatus] = useState('');
  const [semNcm, setSemNcm] = useState(false);
  const [semCst, setSemCst] = useState(false);
  const [pagina, setPagina] = useState(1);
  const LIMITE = 50;

  const filtros = {
    busca, grupo_id: grupoId, subgrupo_id: subgrupoId, fornecedor_id: fornecedorId, estoque_status: estoqueStatus,
    sem_ncm: semNcm ? 'true' : undefined, sem_cst: semCst ? 'true' : undefined,
  };

  // Qualquer mudança de filtro volta pra página 1 — senão dá pra ficar "preso"
  // numa página 3 que não existe mais depois de um filtro reduzir o total.
  useEffect(() => { setPagina(1); }, [busca, grupoId, subgrupoId, fornecedorId, estoqueStatus, semNcm, semCst]);

  const { data: grupos = [] } = useQuery({
    queryKey: ['grupos'],
    queryFn: () => api.get('/grupos').then((r) => r.data),
  });
  const { data: subgrupos = [] } = useQuery({
    queryKey: ['subgrupos', grupoId],
    queryFn: () => api.get('/subgrupos', { params: grupoId ? { grupo_id: grupoId } : {} }).then((r) => r.data),
  });
  // Lista COMPLETA de subgrupos (sem filtro de grupo) — usada só pra montar
  // as opções da célula editável de Subgrupo na grade (cada linha filtra
  // pelo próprio grupo_id do produto, que pode ser diferente do filtro
  // "Subgrupo: todos" selecionado no topo).
  const { data: subgruposTodos = [] } = useQuery({
    queryKey: ['subgrupos-todos'],
    queryFn: () => api.get('/subgrupos').then((r) => r.data),
  });
  const { data: fornecedores = [] } = useQuery({
    queryKey: ['fornecedores'],
    queryFn: () => api.get('/fornecedores').then((r) => r.data),
  });

  const { data: produtos = [], isLoading } = useQuery({
    queryKey: ['produtos', filtros, pagina],
    queryFn: () => api.get('/produtos', { params: { ...filtros, pagina, limite: LIMITE } }).then((r) => r.data),
  });
  const totalRegistros = produtos[0]?.total_registros ? parseInt(produtos[0].total_registros) : produtos.length;
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / LIMITE));

  // A lista na tela é paginada (limite padrão do backend) — pra exportar
  // "tudo" que bate com o filtro, busca à parte com todos=true só quando o
  // modal abre (não fica pedindo isso toda hora à toa).
  const { data: produtosExport = [] } = useQuery({
    queryKey: ['produtos-export', filtros],
    queryFn: () => api.get('/produtos', { params: { ...filtros, todos: 'true' } }).then((r) => r.data),
    enabled: modalExportar,
  });

  // Lista de produtos "Pesado no caixa" — independente dos filtros da tela,
  // pra imprimir/colar perto da balança do caixa.
  const [modalPesados, setModalPesados] = useState(false);
  const [modalBalanca, setModalBalanca] = useState(false);
  const { data: produtosPesados = [] } = useQuery({
    queryKey: ['produtos-pesados'],
    queryFn: () => api.get('/produtos', { params: { pesado_no_caixa: 'true', todos: 'true' } })
      .then((r) => [...r.data].sort((a, b) => String(a.nome).localeCompare(String(b.nome), 'pt-BR'))),
    enabled: modalPesados,
  });

  const formatBRL = (v) => Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const fmtDTCurta = (d) => new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

  // Edição inline da grade (NCM, CST/CSOSN, Grupo, Subgrupo) — 1 campo por
  // vez, via PATCH /produtos/:id/campo-grade. Ao salvar, reconsulta a lista
  // (grade pequena/paginada — não compensa reconciliar o cache na mão,
  // principalmente pro CST/CSOSN, cujo valor exibido é resolvido no backend
  // com uma regra que não dá pra recalcular no front com segurança).
  const [erroEdicao, setErroEdicao] = useState('');
  const salvarCampo = useMutation({
    mutationFn: ({ id, campo, valor }) => api.patch(`/produtos/${id}/campo-grade`, { campo, valor }),
    onSuccess: () => { setErroEdicao(''); queryClient.invalidateQueries({ queryKey: ['produtos'] }); },
    onError: (err) => setErroEdicao(err.response?.data?.erro || 'Erro ao salvar.'),
  });

  // Botão "APLICAR CEST" — roda no backend pra TODOS os produtos já
  // cadastrados (não só a página/filtro atual), preenchendo o CEST a
  // partir do NCM onde o match é inequívoco (ver aplicarCest no backend).
  // Nunca sobrescreve CEST já existente; ambíguo/sem-match fica em branco.
  const [aplicandoCest, setAplicandoCest] = useState(false);
  const [resultadoCest, setResultadoCest] = useState(null);
  const aplicarCest = async () => {
    setAplicandoCest(true); setErroEdicao(''); setResultadoCest(null);
    try {
      const { data } = await api.post('/produtos/aplicar-cest');
      setResultadoCest(data);
      queryClient.invalidateQueries({ queryKey: ['produtos'] });
    } catch (err) {
      setErroEdicao(err.response?.data?.erro || 'Erro ao aplicar CEST.');
    } finally { setAplicandoCest(false); }
  };

  const limparFiltros = () => {
    setGrupoId(''); setSubgrupoId(''); setFornecedorId(''); setEstoqueStatus('');
    setSemNcm(false); setSemCst(false);
  };
  const temFiltroAvancado = grupoId || subgrupoId || fornecedorId || estoqueStatus || semNcm || semCst;

  return (
    <div>
      <div className={styles.topbar}>
        <h2 className={styles.titulo}>Produtos</h2>
        <div className={styles.acoes}>
          <input className={styles.busca} placeholder="Buscar produto..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          <button className={styles.btnSecundario} onClick={() => setModalExportar(true)} disabled={!produtos.length}>
            📤 Exportar
          </button>
          <button className={styles.btnSecundario} onClick={() => setModalPesados(true)}
            title="Lista dos produtos marcados como Pesado no caixa (cód. interno, descrição, unidade e preço)">
            ⚖️ Lista de Pesados
          </button>
          <button className={styles.btnSecundario} onClick={() => setModalBalanca(true)}
            title="Gera o arquivo que o programa da balança etiquetadora importa (produtos marcados como Produto de balança)">
            ⚖️ Exportar p/ Balança
          </button>
        </div>
      </div>

      <div className={styles.acoes} style={{ marginBottom: 14 }}>
        <select className={styles.select} value={grupoId}
          onChange={(e) => { setGrupoId(e.target.value); setSubgrupoId(''); }}>
          <option value="">Grupo: todos</option>
          {grupos.map((g) => <option key={g.id} value={g.id}>{g.nome}</option>)}
        </select>
        <select className={styles.select} value={subgrupoId} onChange={(e) => setSubgrupoId(e.target.value)}
          disabled={!grupoId}>
          <option value="">Subgrupo: todos</option>
          {subgrupos.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
        </select>
        <select className={styles.select} value={fornecedorId} onChange={(e) => setFornecedorId(e.target.value)}>
          <option value="">Fornecedor: todos</option>
          {fornecedores.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
        </select>
        <select className={styles.select} value={estoqueStatus} onChange={(e) => setEstoqueStatus(e.target.value)}>
          {ESTOQUE_STATUS_OPCOES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: 'var(--text)' }}>
          <input type="checkbox" checked={semNcm} onChange={(e) => setSemNcm(e.target.checked)} />
          Sem NCM
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.85rem', color: 'var(--text)' }}>
          <input type="checkbox" checked={semCst} onChange={(e) => setSemCst(e.target.checked)} />
          Sem CST/CSOSN
        </label>
        {temFiltroAvancado && (
          <button className={styles.btnSecundario} onClick={limparFiltros}>Limpar filtros</button>
        )}
        <button className={styles.btnSecundario} onClick={aplicarCest} disabled={aplicandoCest}
          title="Preenche o CEST dos produtos já cadastrados a partir do NCM, quando o match é inequívoco">
          {aplicandoCest ? 'Aplicando CEST...' : 'APLICAR CEST'}
        </button>
      </div>

      {erroEdicao && <div className={styles.erro} style={{ marginBottom: 10 }}>{erroEdicao}</div>}
      {resultadoCest && (
        <div className={styles.sucesso} style={{ marginBottom: 10 }}>
          CEST aplicado a {resultadoCest.atualizados} de {resultadoCest.elegiveis} produto(s) elegível(is)
          {resultadoCest.ambiguos > 0 && ` — ${resultadoCest.ambiguos} ficaram em branco por ambiguidade (mesmo NCM em mais de um segmento)`}
          {resultadoCest.sem_match > 0 && ` — ${resultadoCest.sem_match} sem CEST correspondente encontrado`}.
        </div>
      )}

      {isLoading ? (
        <p>Carregando...</p>
      ) : (
        <div className={styles.tabela}>
          <table>
            <thead>
              <tr>
                <th>Cód.</th><th>Nome</th><th>Cód. Barras</th>
                <th>NCM</th><th>CST/CSOSN</th><th>CEST</th><th>Obs. Fiscal</th><th>Grupo</th><th>Subgrupo</th>
                <th>Preço Venda</th><th>Preço Custo</th><th style={{textAlign:'right'}}>Estoque Atual</th>
              </tr>
            </thead>
            <tbody>
              {produtos.map((p, indice) => {
                const qtd = parseFloat(p.estoque_atual ?? 0);
                const minimo = parseFloat(p.estoque_minimo ?? 0);
                const baixo = minimo > 0 && qtd < minimo;
                const subgruposDoGrupo = subgruposTodos.filter((s) => String(s.grupo_id) === String(p.grupo_id));
                return (
                  <tr key={p.id}>
                    <td style={{fontFamily:'monospace',color:'#666',fontSize:'0.82rem'}}>{p.codigo_interno}</td>
                    <td><strong>{p.nome}</strong></td>
                    <td>{p.codigo_barras || '—'}</td>
                    <CelulaEditavel
                      valor={p.ncm} exibicao={p.ncm} linha={indice} coluna="ncm"
                      onSalvar={(valor) => salvarCampo.mutate({ id: p.id, campo: 'ncm', valor })}
                    />
                    <CelulaEditavel
                      valor={p.cst_icms} exibicao={p.cst_icms} linha={indice} coluna="cst_icms"
                      onSalvar={(valor) => salvarCampo.mutate({ id: p.id, campo: 'cst_icms', valor })}
                    />
                    <CelulaEditavel
                      valor={p.cest} exibicao={p.cest} linha={indice} coluna="cest"
                      subinfo={p.cest_aplicado_em ? `aplicado ${fmtDTCurta(p.cest_aplicado_em)}` : null}
                      onSalvar={(valor) => salvarCampo.mutate({ id: p.id, campo: 'cest', valor })}
                    />
                    <td style={{fontSize:'0.78rem',color:'#555',maxWidth:'220px'}} title={p.obs_fiscal || ''}>
                      {p.obs_fiscal || '—'}
                    </td>
                    <CelulaEditavel
                      tipo="select" valor={p.grupo_id} exibicao={p.grupo_nome} opcoes={grupos}
                      onSalvar={(valor) => salvarCampo.mutate({ id: p.id, campo: 'grupo_id', valor })}
                    />
                    <CelulaEditavel
                      tipo="select" valor={p.subgrupo_id} exibicao={p.subgrupo_nome} opcoes={subgruposDoGrupo}
                      onSalvar={(valor) => salvarCampo.mutate({ id: p.id, campo: 'subgrupo_id', valor })}
                    />
                    <td>{formatBRL(p.preco_venda)}</td>
                    <td>{formatBRL(p.preco_custo)}</td>
                    <td style={{textAlign:'right'}}>
                      <span className={baixo ? styles.estoqueAlerta : ''}
                        style={{fontFamily:'monospace',fontWeight:600,color: baixo ? '#d32f2f' : '#2e7d32'}}>
                        {fmtQtd(qtd, p.unidade_fraciona)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && produtos.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 14 }}>
          <button className={styles.btnSecundario} disabled={pagina <= 1}
            onClick={() => setPagina((p) => Math.max(1, p - 1))}>← Anterior</button>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Página {pagina} de {totalPaginas} ({totalRegistros} produto{totalRegistros === 1 ? '' : 's'})
          </span>
          <button className={styles.btnSecundario} disabled={pagina >= totalPaginas}
            onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}>Próxima →</button>
        </div>
      )}

      {modalExportar && (
        <ModalExportarLista
          titulo="Produtos"
          colunas={COLUNAS_EXPORT}
          dados={produtosExport}
          nomeArquivo={`produtos_${new Date().toISOString().slice(0, 10)}`}
          onClose={() => setModalExportar(false)}
        />
      )}

      {modalPesados && (
        <ModalExportarLista
          titulo="Produtos Pesados no Caixa"
          colunas={COLUNAS_PESADOS}
          dados={produtosPesados}
          nomeArquivo={`produtos_pesados_${new Date().toISOString().slice(0, 10)}`}
          onClose={() => setModalPesados(false)}
        />
      )}

      {modalBalanca && <ModalExportarBalanca onClose={() => setModalBalanca(false)} />}
    </div>
  );
}
